import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Check, CornerDownRight, MessageSquare, ChevronRight, ChevronDown, ShieldCheck } from "lucide-react";

function CommentsSettingsCard({ mode, onChange, saving }) {
  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center justify-between">
          <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sky-700"/>Comments settings</span>
          <span className="text-xs uppercase tracking-widest text-slate-400 font-normal">
            {saving ? "Saving…" : "Auto-saves"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-3">
          Choose when reader comments become visible on your blogs. Admin replies are always posted immediately.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <label data-testid="comments-mode-auto"
                 className={`cursor-pointer border rounded-lg p-4 transition-colors ${mode === "auto" ? "border-sky-700 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="flex items-start gap-3">
              <input type="radio" name="cmode" value="auto" checked={mode === "auto"} onChange={() => onChange("auto")}
                     className="w-4 h-4 accent-sky-700 mt-1"/>
              <div>
                <div className="font-heading font-semibold text-slate-900">Post automatically</div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Comments appear instantly on the blog. Admins still see them in the queue for oversight.
                </p>
              </div>
            </div>
          </label>
          <label data-testid="comments-mode-review"
                 className={`cursor-pointer border rounded-lg p-4 transition-colors ${mode === "review" ? "border-sky-700 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="flex items-start gap-3">
              <input type="radio" name="cmode" value="review" checked={mode === "review"} onChange={() => onChange("review")}
                     className="w-4 h-4 accent-sky-700 mt-1"/>
              <div>
                <div className="font-heading font-semibold text-slate-900">Post after review</div>
                <p className="text-xs text-slate-500 mt-0.5">
                  New comments are hidden until an admin approves them. Posters see their own comment marked <em>Pending</em>.
                </p>
              </div>
            </div>
          </label>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CommentsPanel() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({ total: 0, unreviewed: 0, pending: 0 });
  const [settings, setSettings] = useState({ moderation: "auto" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [showReviewed, setShowReviewed] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = () => {
    api.get("/admin/comments").then(r => setRows(r.data));
    api.get("/admin/comments/counts").then(r => setCounts(r.data));
  };
  const loadSettings = () => api.get("/site-settings/comments").then(r => setSettings(r.data));
  useEffect(() => { load(); loadSettings(); }, []);

  const setMode = async (mode) => {
    const prev = settings.moderation;
    setSettings({ moderation: mode });
    setSavingSettings(true);
    try {
      await api.put("/site-settings/comments", { moderation: mode });
      toast.success(mode === "review" ? "Reader comments now require approval." : "Reader comments post immediately.");
    } catch (e) {
      setSettings({ moderation: prev });
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSavingSettings(false); }
  };

  const approve = async (id) => {
    try { await api.put(`/comments/${id}/approve`); toast.success("Approved"); load(); }
    catch { toast.error("Could not approve"); }
  };
  const markReviewed = async (id, reviewed) => {
    try { await api.put(`/comments/${id}/reviewed`, { reviewed, approve: false });
          toast.success(reviewed ? "Marked reviewed" : "Marked unreviewed"); load(); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this comment (and any replies)? This cannot be undone.")) return;
    try { await api.delete(`/comments/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };
  const submitReply = async (blogId, parentId) => {
    const body = replyText.trim();
    if (!body) return;
    try {
      await api.post(`/blogs/${blogId}/comments`, { body, parent_comment_id: parentId });
      toast.success("Reply posted");
      setReplyingTo(null); setReplyText("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post reply"); }
  };

  if (rows === null) return <div className="mt-6 text-slate-500">Loading…</div>;

  const pendingReview = rows.filter(c => !c.reviewed_at);
  const pendingApprovalCount = pendingReview.filter(c => c.approved === false).length;
  const reviewed = rows.filter(c => c.reviewed_at);
  const hasPendingApproval = pendingApprovalCount > 0;

  return (
    <div className="space-y-6">
      <CommentsSettingsCard mode={settings.moderation} onChange={setMode} saving={savingSettings}/>

      {/* Pending review — unified bucket of everything the admin hasn't triaged yet */}
      <Card className={hasPendingApproval ? "border-amber-200" : "border-slate-200"}>
        <CardHeader>
          <CardTitle className="font-heading flex items-center justify-between gap-2 flex-wrap">
            <span className="inline-flex items-center gap-2">
              {hasPendingApproval && <span className="w-2 h-2 rounded-full bg-amber-500 inline-block"/>}
              Pending review <span className="text-slate-400 font-normal">· {pendingReview.length}</span>
            </span>
            <span className="text-xs uppercase tracking-widest text-slate-500 font-normal">
              {counts.total} total
              {hasPendingApproval && (
                <> · <span className="text-amber-700 font-semibold">{pendingApprovalCount} awaiting approval</span></>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingReview.length === 0 ? (
            <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">
              <MessageSquare className="w-6 h-6 mx-auto text-slate-300 mb-2"/>
              All caught up — nothing pending review.
            </div>
          ) : (
            <ul className="divide-y divide-slate-200" data-testid="admin-comments-pending">
              {pendingReview.map(c => renderComment(c))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Reviewed — collapsed by default */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center justify-between gap-2 flex-wrap">
            <button data-testid="comments-reviewed-toggle" onClick={() => setShowReviewed(v => !v)}
                    className="flex items-center gap-2 text-slate-800 hover:text-sky-700">
              {showReviewed ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
              <span>Reviewed <span className="text-slate-400 font-normal">· {reviewed.length}</span></span>
            </button>
            {reviewed.length > 0 && !showReviewed && (
              <span className="text-xs uppercase tracking-widest text-slate-400">Click to view</span>
            )}
          </CardTitle>
        </CardHeader>
        {showReviewed && (
          <CardContent>
            {reviewed.length === 0
              ? <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">No reviewed comments yet.</div>
              : <ul className="divide-y divide-slate-200" data-testid="admin-comments-reviewed">
                  {reviewed.map(c => renderComment(c))}
                </ul>}
          </CardContent>
        )}
      </Card>
    </div>
  );

  function renderComment(c) {
    const isPending = c.approved === false;
    const isReviewed = !!c.reviewed_at;
    return (
      <li key={c.comment_id} data-testid={`admin-comment-${c.comment_id}`} className="py-5">
        <div className="flex items-start gap-3">
          {c.user_picture
            ? <img src={c.user_picture} alt="" className="w-9 h-9 rounded-full"/>
            : <div className="w-9 h-9 rounded-full bg-slate-200"/>}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="font-semibold text-slate-900">{c.user_name || "Anonymous"}</span>
              <span className="text-slate-400">·</span>
              <Link to={`/blogs/${c.blog_slug}`} className="text-sky-700 hover:text-sky-900 truncate">{c.blog_title}</Link>
              {isPending && (
                <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">
                  Pending
                </span>
              )}
              <span className="text-slate-400 ml-auto text-xs">{new Date(c.created_at).toLocaleString()}</span>
            </div>
            <p className="text-slate-800 mt-1 whitespace-pre-wrap">{c.body}</p>

            <div className="mt-2 flex items-center gap-3 text-xs uppercase tracking-widest flex-wrap">
              {isPending && (
                <button data-testid={`admin-comment-approve-${c.comment_id}`} onClick={() => approve(c.comment_id)}
                        className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
                  <Check className="w-3.5 h-3.5"/>Approve
                </button>
              )}
              <button data-testid={`admin-comment-reply-${c.comment_id}`}
                      onClick={() => { setReplyingTo(c.comment_id === replyingTo ? null : c.comment_id); setReplyText(""); }}
                      className="text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
                <CornerDownRight className="w-3.5 h-3.5"/>{c.comment_id === replyingTo ? "Cancel" : "Reply"}
              </button>
              {!isPending && (
                <button data-testid={`admin-comment-review-${c.comment_id}`}
                        onClick={() => markReviewed(c.comment_id, !isReviewed)}
                        className={`inline-flex items-center gap-1 ${isReviewed ? "text-slate-500 hover:text-slate-900" : "text-emerald-700 hover:text-emerald-900"}`}>
                  <Check className="w-3.5 h-3.5"/>
                  {isReviewed ? "Un-review" : "Mark reviewed"}
                </button>
              )}
              <button data-testid={`admin-comment-delete-${c.comment_id}`} onClick={() => remove(c.comment_id)}
                      className="text-red-500 hover:text-red-700 inline-flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5"/>Delete
              </button>
            </div>

            {replyingTo === c.comment_id && (
              <div className="mt-3 border-l-2 border-sky-600 pl-3">
                <Textarea data-testid={`admin-comment-reply-input-${c.comment_id}`}
                          rows={3} value={replyText} onChange={e => setReplyText(e.target.value)}
                          placeholder="Write an admin reply…"/>
                {(isPending || !isReviewed) && (
                  <p className="mt-1.5 text-xs text-slate-500 flex items-center gap-1">
                    <Check className="w-3 h-3 text-emerald-600"/>
                    Posting will also {isPending ? "approve and " : ""}mark this comment reviewed.
                  </p>
                )}
                <div className="mt-2 flex justify-end gap-2">
                  <button onClick={() => { setReplyingTo(null); setReplyText(""); }}
                          className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900">Cancel</button>
                  <button data-testid={`admin-comment-reply-submit-${c.comment_id}`}
                          onClick={() => submitReply(c.blog_id, c.comment_id)}
                          className="text-xs uppercase tracking-widest bg-sky-700 text-white px-3 py-1.5 rounded hover:bg-sky-800 inline-flex items-center gap-1">
                    <CornerDownRight className="w-3.5 h-3.5"/>
                    {isPending ? "Reply and approve" : (isReviewed ? "Post reply" : "Reply and mark reviewed")}
                  </button>
                </div>
              </div>
            )}

            {c.replies && c.replies.length > 0 && (
              <ul className="mt-4 space-y-3 border-l-2 border-slate-200 pl-4">
                {c.replies.map(rp => (
                  <li key={rp.comment_id} className="text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{rp.user_name}</span>
                      {rp.is_admin_reply && (
                        <span className="text-[10px] uppercase tracking-widest bg-sky-700 text-white rounded px-1.5 py-0.5">Admin</span>
                      )}
                      <span className="text-slate-400 text-xs">{new Date(rp.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{rp.body}</div>
                    <button onClick={() => remove(rp.comment_id)}
                            className="text-xs text-red-500 hover:text-red-700 mt-1 uppercase tracking-widest inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3"/>Delete reply
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </li>
    );
  }
}
